import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { useAuth } from "@/hooks/useAuth";
import { ArrowRight, CalendarCheck, CalendarX, Plug, RefreshCw, Loader2, LogOut, Check, X, Trash2, Eraser, CalendarPlus, CalendarSearch, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import ContentEditor from "@/components/ContentEditor";
import AnalyticsTab from "@/components/AnalyticsTab";
import PortfolioTab from "@/components/PortfolioTab";
import SectionsTab from "@/components/SectionsTab";
import AvailabilityTab from "@/components/AvailabilityTab";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function AdminPage() {
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  useDocumentMeta({
    title: "Admin Dashboard · Northwestern Exterior Cleaning",
    description: "Owner dashboard — bookings, availability, pricing and content.",
    path: "/admin",
  });
  const [status, setStatus] = useState({ connected: false });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/oauth/calendar/status", { withCredentials: true }),
        api.get("/bookings", { withCredentials: true }),
      ]);
      setStatus(s.data);
      setBookings(b.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status }, { withCredentials: true });
      toast.success(
        status === "confirmed" ? "Booking confirmed" :
        status === "denied" ? "Booking denied. Slot freed." :
        status === "cancelled" ? "Booking cancelled" :
        "Booking updated"
      );
      load();
    } catch {
      toast.error("Could not update booking");
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(
      `Delete booking for ${b.customer_name} on ${b.date}?\n\nThis removes the booking + any Google Calendar events. Cannot be undone.`
    )) return;
    try {
      await api.delete(`/bookings/${b.id}`, { withCredentials: true });
      toast.success("Booking deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const cleanupTestBookings = async () => {
    if (!window.confirm(
      "Delete ALL test bookings?\n\nThis wipes every booking whose name starts with 'TEST' or whose email ends with @example.com or @test.com, AND removes their Google Calendar events. Cannot be undone."
    )) return;
    try {
      const { data } = await api.post("/admin/bookings/cleanup-tests", {}, { withCredentials: true });
      const cal = data.calendar_events_deleted ?? 0;
      const failed = data.calendar_events_failed ?? 0;
      const msg = [
        `Deleted ${data.deleted_count} test booking${data.deleted_count === 1 ? "" : "s"}`,
        cal > 0 ? `Removed ${cal} calendar event${cal === 1 ? "" : "s"}` : null,
        failed > 0 ? `${failed} calendar event${failed === 1 ? "" : "s"} failed to delete — check logs` : null,
      ].filter(Boolean).join(" · ");
      if (failed > 0) toast.warning(msg);
      else toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    }
  };

  const wipeAllBookings = async () => {
    if (!window.confirm(
      "WIPE ALL bookings?\n\nThis PERMANENTLY deletes every estimate visit and wash from the database AND removes their Google Calendar events. Cannot be undone.\n\nContinue?"
    )) return;
    if (!window.confirm("Are you absolutely sure? Type 'yes' below on the next confirmation.")) return;
    try {
      const { data } = await api.post("/admin/bookings/wipe-all", {}, { withCredentials: true });
      toast.success(`Wiped ${data.deleted_count} booking(s) · ${data.calendar_events_deleted || 0} calendar event(s) removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
  };

  // Convert-to-wash dialog state
  const [convertDialogFor, setConvertDialogFor] = useState(null);   // visit booking object
  const [convertForm, setConvertForm] = useState({ sqft: 1500, material: "concrete", stain_severity: "light", scheduled_dates: "", notes: "" });
  const [converting, setConverting] = useState(false);

  const openConvertDialog = (visit) => {
    setConvertForm({
      sqft: 1500,
      material: "concrete",
      stain_severity: "light",
      scheduled_dates: "",
      notes: visit.notes || "",
    });
    setConvertDialogFor(visit);
  };

  const submitConvert = async () => {
    if (!convertDialogFor) return;
    const dates = convertForm.scheduled_dates
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
    if (dates.length === 0) {
      toast.error("Enter at least one scheduled date (YYYY-MM-DD)");
      return;
    }
    setConverting(true);
    try {
      await api.post(
        `/admin/visits/${convertDialogFor.id}/convert`,
        {
          sqft: Number(convertForm.sqft),
          material: convertForm.material,
          stain_severity: convertForm.stain_severity,
          scheduled_dates: dates,
          notes: convertForm.notes,
        },
        { withCredentials: true }
      );
      toast.success(`Converted ${convertDialogFor.customer_name}'s visit into a scheduled wash`);
      setConvertDialogFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setConverting(false);
    }
  };

  const syncBookingToCalendar = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/sync-calendar`, {}, { withCredentials: true });
      toast.success(`Synced ${b.customer_name}'s booking to Google Calendar`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync to calendar");
    }
  };

  // Per-booking calendar truth check (queried lazily). Maps booking.id → result.
  const [calStatusById, setCalStatusById] = useState({});
  const [verifyingId, setVerifyingId] = useState(null);

  const verifyBookingCalendar = async (b) => {
    setVerifyingId(b.id);
    try {
      const { data } = await api.get(`/bookings/${b.id}/calendar-status`, { withCredentials: true });
      setCalStatusById((prev) => ({ ...prev, [b.id]: data }));
      const present = (data.events || []).filter((e) => e.exists).length;
      const total = (data.events || []).length;
      if (!data.connected) {
        toast.error(`Google Calendar isn't connected — connect it first.`);
      } else if (total === 0) {
        toast.info("This booking has no Google event IDs stored.");
      } else if (present === total) {
        toast.success(`Verified: ${present}/${total} event(s) live on ${data.connected_email || "the connected calendar"}.`);
      } else if (present === 0) {
        toast.warning(`None of the ${total} event(s) exist on ${data.connected_email || "the connected calendar"}. Badge corrected to "Not synced".`);
      } else {
        toast.warning(`${present}/${total} event(s) found on ${data.connected_email || "the connected calendar"}.`);
      }
      if (data.updated_calendar_synced) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify");
    } finally {
      setVerifyingId(null);
    }
  };

  useEffect(() => {
    if (params.get("connected") === "1") toast.success("Google Calendar connected!");
    if (params.get("error")) toast.error("Calendar connection failed");
    load();
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/calendar/login", { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error("Could not start OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.post("/oauth/calendar/disconnect", null, { withCredentials: true });
    toast.success("Disconnected");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 overflow-x-hidden" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div className="min-w-0">
          <p className="overline text-muted-foreground">Owner</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter mt-3">Dashboard</h1>
          <p className="text-muted-foreground mt-3 break-words">
            {user?.email && <>Signed in as <span className="text-foreground font-medium break-all">{user.email}</span>. </>}
            Connect Google Calendar so bookings sync automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <Button variant="outline" onClick={load} data-testid="admin-refresh" className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="ghost" onClick={logout} data-testid="admin-logout" className="rounded-full">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      <Card className="p-6 sm:p-8 border-border bg-white mb-8" data-testid="calendar-connect-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4 sm:gap-5 min-w-0">
            <div
              className={`w-12 h-12 shrink-0 rounded-xl grid place-items-center ${
                status.connected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
            >
              {status.connected ? <CalendarCheck className="w-5 h-5" /> : <CalendarX className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <p className="font-display text-lg sm:text-xl font-semibold tracking-tight">
                {status.connected ? "Google Calendar connected" : "Google Calendar not connected"}
              </p>
              <p className="text-sm text-muted-foreground mt-1 break-words">
                {status.connected
                  ? <>Bookings will sync to <span className="break-all">{status.email}</span>'s primary calendar.</>
                  : "Connect once to receive bookings as calendar events."}
              </p>
            </div>
          </div>

          {status.connected ? (
            <Button data-testid="oauth-disconnect" variant="outline" onClick={disconnect} className="rounded-full">
              Disconnect
            </Button>
          ) : (
            <Button data-testid="oauth-connect" onClick={connect} disabled={connecting} className="btn-primary rounded-full px-6">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
              Connect Google Calendar
            </Button>
          )}
        </div>
      </Card>

      <Tabs defaultValue="analytics" data-testid="admin-tabs">
        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
          <TabsList className="bg-muted/60 flex-wrap h-auto justify-start">
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Estimate visits</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Edit copy</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab />
        </TabsContent>

        <TabsContent value="availability" className="mt-6">
          <AvailabilityTab />
        </TabsContent>

        <TabsContent value="portfolio" className="mt-6">
          <PortfolioTab />
        </TabsContent>

        <TabsContent value="sections" className="mt-6">
          <SectionsTab />
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-2xl font-semibold tracking-tight">Estimate visits & washes</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground" data-testid="admin-bookings-count">{bookings.length} total</span>
          <Button
            variant="outline"
            size="sm"
            onClick={cleanupTestBookings}
            data-testid="cleanup-test-bookings"
            className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
          >
            <Eraser className="w-3.5 h-3.5 mr-1.5" /> Clean up test bookings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={wipeAllBookings}
            data-testid="wipe-all-bookings"
            className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Wipe all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border" data-testid="admin-empty">
          <p className="text-muted-foreground">No bookings yet. They'll show up here as customers book.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {bookings.map((b) => {
            const statusColor =
              b.status === "confirmed" ? "bg-emerald-700 text-white" :
              b.status === "denied" ? "bg-rose-700 text-white" :
              b.status === "cancelled" ? "bg-muted text-muted-foreground" :
              "bg-amber-500 text-white"; // pending
            return (
            <Card key={b.id} data-testid={`admin-booking-${b.id}`} className="p-5 sm:p-6 border-border bg-white hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold break-words">{b.customer_name}</span>
                    <Badge
                      data-testid={`booking-type-${b.id}`}
                      className={b.type === "wash" ? "bg-primary text-primary-foreground" : "bg-sky-600 text-white"}
                    >
                      {b.type === "wash" ? "Wash" : "Estimate visit"}
                    </Badge>
                    <Badge data-testid={`booking-status-${b.id}`} className={`${statusColor} capitalize`}>
                      {b.status || "pending"}
                    </Badge>
                    <Badge variant={b.calendar_synced ? "default" : "secondary"} className={b.calendar_synced ? "bg-primary" : ""}>
                      {b.calendar_synced ? "Synced" : "Not synced"}
                    </Badge>
                    {b.type === "estimate_visit" && b.visit_status === "converted" && (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-800 bg-emerald-50">Converted</Badge>
                    )}
                    {b.type === "wash" && b.tier && <Badge variant="outline">{b.tier}</Badge>}
                    {b.type === "wash" && b.material && (
                      <Badge variant="outline" className="capitalize">{b.material}</Badge>
                    )}
                    {b.type === "wash" && b.stain_severity && b.stain_severity !== "light" && (
                      <Badge variant="outline" className="capitalize">{b.stain_severity} stain</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 break-words">{b.address}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground mt-3">
                    <span>
                      {(b.scheduled_dates && b.scheduled_dates.length > 0)
                        ? (b.scheduled_dates.length > 1
                            ? `${format(new Date(b.scheduled_dates[0]), "MMM d")} → ${format(new Date(b.scheduled_dates[b.scheduled_dates.length - 1]), "MMM d")} (${b.scheduled_dates.length} days)`
                            : format(new Date(b.scheduled_dates[0]), "EEE, MMM d"))
                        : `${format(new Date(b.date), "EEE, MMM d")}${b.time_slot ? ` · ${b.time_slot}` : ""}`}
                    </span>
                    {b.type === "wash" && b.estimated_duration_high_hours > 0 && (
                      <span>~{b.estimated_duration_low_hours} to {b.estimated_duration_high_hours}h</span>
                    )}
                    {b.type === "wash" && b.sqft > 0 && <span>{b.sqft} sqft</span>}
                    <span className="break-all">{b.customer_email}</span>
                    <span>{b.customer_phone}</span>
                  </div>
                  {b.notes && <p className="text-xs text-muted-foreground italic mt-2 break-words">&ldquo;{b.notes}&rdquo;</p>}
                </div>
                <div className="flex flex-col items-start lg:items-end gap-3 min-w-0">
                  {b.type === "wash" ? (
                    <div className="text-left lg:text-right">
                      {b.estimate_low > 0 && b.estimate_high > 0 ? (
                        <>
                          <p className="font-display text-2xl font-semibold" data-testid={`booking-range-${b.id}`}>
                            ${Math.round(b.estimate_low)} to ${Math.round(b.estimate_high)}
                          </p>
                          <p className="text-xs text-muted-foreground">est. range</p>
                        </>
                      ) : b.total_quote > 0 ? (
                        <>
                          <p className="font-display text-2xl font-semibold">${(b.final_total ?? b.total_quote).toFixed(0)}</p>
                          <p className="text-xs text-muted-foreground">
                            {b.rate ? `$${b.rate.toFixed(2)}/sqft` : "quote"}
                          </p>
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-left lg:text-right">
                      <p className="font-display text-sm font-medium text-muted-foreground">30-min visit</p>
                      {b.type === "estimate_visit" && b.visit_status === "converted" && b.converted_to_booking_id && (
                        <p className="text-xs text-emerald-700 mt-1">→ scheduled as wash</p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    {b.type === "estimate_visit" && b.visit_status !== "converted" && b.status !== "denied" && b.status !== "cancelled" && (
                      <Button
                        size="sm"
                        onClick={() => openConvertDialog(b)}
                        data-testid={`booking-convert-${b.id}`}
                        title="Convert this visit into a scheduled wash after quoting on-site"
                        className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert to wash
                      </Button>
                    )}
                    {b.calendar_synced && b.status !== "denied" && b.status !== "cancelled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verifyBookingCalendar(b)}
                        data-testid={`booking-verify-calendar-${b.id}`}
                        title="Live-check Google Calendar to confirm the event still exists"
                        disabled={verifyingId === b.id}
                        className="rounded-full text-muted-foreground hover:text-primary hover:bg-primary/5 border-border"
                      >
                        {verifyingId === b.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <CalendarSearch className="w-3.5 h-3.5 mr-1" />
                        )}
                        Verify
                      </Button>
                    )}
                    {!b.calendar_synced && b.status !== "denied" && b.status !== "cancelled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => syncBookingToCalendar(b)}
                        data-testid={`booking-sync-calendar-${b.id}`}
                        title="Create the missing Google Calendar event for this booking"
                        className="rounded-full text-primary hover:text-primary border-primary/30 hover:bg-primary/5"
                      >
                        <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Sync to Calendar
                      </Button>
                    )}
                    {b.status !== "confirmed" && (
                      <Button
                        size="sm"
                        onClick={() => setBookingStatus(b.id, "confirmed")}
                        data-testid={`booking-confirm-${b.id}`}
                        className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                      </Button>
                    )}
                    {b.status !== "denied" && b.status !== "cancelled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBookingStatus(b.id, "denied")}
                        data-testid={`booking-deny-${b.id}`}
                        className="rounded-full text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200"
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Deny
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteBooking(b)}
                      data-testid={`booking-delete-${b.id}`}
                      title="Delete booking"
                      className="rounded-full text-muted-foreground hover:text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              {calStatusById[b.id] && (
                <div className="mt-4 pt-4 border-t border-border" data-testid={`booking-calendar-verify-${b.id}`}>
                  <p className="text-xs text-muted-foreground mb-2">
                    Live Google Calendar check
                    {calStatusById[b.id].connected_email && (
                      <> · connected as <strong className="break-all">{calStatusById[b.id].connected_email}</strong></>
                    )}
                  </p>
                  {(calStatusById[b.id].events || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No event IDs stored on this booking.</p>
                  ) : (
                    <ul className="space-y-1.5 text-xs">
                      {calStatusById[b.id].events.map((evt) => (
                        <li key={evt.event_id} className="flex items-start gap-2">
                          <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${evt.exists ? "bg-emerald-600" : "bg-rose-600"}`} />
                          <span className="flex-1 min-w-0 break-all">
                            {evt.exists ? (
                              <>
                                <span className="text-emerald-700 font-medium">Found</span> · {evt.summary || "(no title)"} ·{" "}
                                {evt.html_link ? (
                                  <a
                                    href={evt.html_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`booking-calendar-link-${b.id}-${evt.event_id}`}
                                    className="text-primary hover:underline inline-flex items-center gap-1"
                                  >
                                    Open in Google Calendar <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span className="font-mono text-muted-foreground">{evt.event_id}</span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="text-rose-700 font-medium">Missing</span> · event id{" "}
                                <span className="font-mono text-muted-foreground">{evt.event_id}</span>
                                {evt.error && <> ({evt.error})</>}
                              </>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}
        </TabsContent>


        <TabsContent value="content" className="mt-6">
          <ContentEditor />
        </TabsContent>
      </Tabs>

      {/* Convert-to-wash dialog */}
      <Dialog open={!!convertDialogFor} onOpenChange={(open) => !open && setConvertDialogFor(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Convert visit into a scheduled wash</DialogTitle>
            <DialogDescription>
              {convertDialogFor && (
                <>Measured on-site for <strong>{convertDialogFor.customer_name}</strong> at {convertDialogFor.address}. Fill in what you measured — a new booking of type <em>wash</em> will be created and synced to Google Calendar.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label htmlFor="cvt-sqft" className="text-sm font-medium">Square footage</Label>
              <Input
                id="cvt-sqft"
                type="number"
                min={1}
                value={convertForm.sqft}
                onChange={(e) => setConvertForm({ ...convertForm, sqft: e.target.value })}
                data-testid="convert-sqft-input"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Surface material</Label>
                <Select value={convertForm.material} onValueChange={(v) => setConvertForm({ ...convertForm, material: v })}>
                  <SelectTrigger className="mt-1" data-testid="convert-material-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["concrete","brick","pavers","composite","wood","hybrid"].map((m) => (
                      <SelectItem key={m} value={m} data-testid={`convert-material-${m}`}>
                        <span className="capitalize">{m}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Stain severity</Label>
                <Select value={convertForm.stain_severity} onValueChange={(v) => setConvertForm({ ...convertForm, stain_severity: v })}>
                  <SelectTrigger className="mt-1" data-testid="convert-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["light","moderate","heavy"].map((s) => (
                      <SelectItem key={s} value={s} data-testid={`convert-severity-${s}`}>
                        <span className="capitalize">{s}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="cvt-dates" className="text-sm font-medium">Scheduled date(s)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Comma-separated YYYY-MM-DD. Use one date for a single-day wash, multiple consecutive for multi-day.</p>
              <Input
                id="cvt-dates"
                type="text"
                value={convertForm.scheduled_dates}
                onChange={(e) => setConvertForm({ ...convertForm, scheduled_dates: e.target.value })}
                placeholder="2026-09-15, 2026-09-16"
                data-testid="convert-dates-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cvt-notes" className="text-sm font-medium">Notes (internal)</Label>
              <Textarea
                id="cvt-notes"
                rows={3}
                value={convertForm.notes}
                onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                data-testid="convert-notes-input"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogFor(null)} data-testid="convert-cancel">Cancel</Button>
            <Button onClick={submitConvert} disabled={converting} data-testid="convert-submit">
              {converting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create wash booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
